import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { openOAuthTab } from "@/lib/oauth";
import { Download, ShieldCheck, Sparkles, Mic, Link2, Loader2, Mail, Calendar, Unplug, Brain, BookText } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

export default function Settings() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [commandments, setCommandments] = useState("");
  const [integrations, setIntegrations] = useState([]);
  const [saving, setSaving] = useState(false);
  // Microsoft 365 connection
  const [msStatus, setMsStatus] = useState(null);
  const [msBusy, setMsBusy] = useState(false);
  // Granola (workspace-level, admin-managed)
  const [granola, setGranola] = useState(null);
  const [gKey, setGKey] = useState("");
  const [gBusy, setGBusy] = useState(false);
  // Assistant settings
  const [asstName, setAsstName] = useState("");
  const [wakeEnabled, setWakeEnabled] = useState(false);
  const [wakeWord, setWakeWord] = useState("");
  const [savingAsst, setSavingAsst] = useState(false);
  // Cross-conversation memory
  const [memEnabled, setMemEnabled] = useState(true);
  const [memDocId, setMemDocId] = useState(null);
  const [memBusy, setMemBusy] = useState(false);
  const [autoLearn, setAutoLearn] = useState(true);
  const [autoBusy, setAutoBusy] = useState(false);

  const loadMsStatus = async () => {
    try {
      const r = await api.get("/integrations/microsoft/status");
      setMsStatus(r.data);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    (async () => {
      try {
        const [cmd, integ, mem] = await Promise.all([
          api.get("/settings/commandments"),
          api.get("/integrations"),
          api.get("/memory").catch(() => null),
        ]);
        setCommandments(cmd.data.commandments || "");
        setIntegrations(integ.data.items || []);
        if (mem) { setMemEnabled(mem.data.enabled); setMemDocId(mem.data.doc_id); }
      } catch { /* ignore */ }
      loadMsStatus();
      loadGranola();
      if (typeof window !== "undefined") {
        const q = window.location.search;
        if (q.includes("ms_connected=1")) {
          toast.success("Microsoft 365 connected. Your email & calendar are now readable.");
          window.history.replaceState(null, "", window.location.pathname);
        } else if (q.includes("ms_error=1")) {
          toast.error("Couldn't connect Microsoft 365. Please try again.");
          window.history.replaceState(null, "", window.location.pathname);
        }
      }
    })();
  }, []);

  useEffect(() => {
    if (user) setAutoLearn(user.auto_learn_enabled !== false);
  }, [user]);

  const connectMicrosoft = async () => {
    setMsBusy(true);
    const tab = openOAuthTab();
    try {
      // When the existing connection is broken, force a fresh interactive
      // sign-in (?reconnect=1 → prompt=login) so federated IdPs (e.g. Okta)
      // re-authenticate and Microsoft mints a clean refresh token.
      const reconnecting = Boolean(msStatus?.connected && msStatus?.needs_reconnect);
      const r = await api.get(`/integrations/microsoft/connect${reconnecting ? "?reconnect=1" : ""}`);
      tab.go(r.data.authorize_url);
    } catch (e) {
      tab.fail();
      toast.error(e?.response?.data?.detail || "Microsoft 365 isn't configured yet.");
      setMsBusy(false);
    }
  };

  const disconnectMicrosoft = async () => {
    setMsBusy(true);
    try {
      await api.post("/integrations/microsoft/disconnect");
      await loadMsStatus();
      toast("Microsoft 365 disconnected.");
    } catch {
      toast.error("Couldn't disconnect.");
    } finally {
      setMsBusy(false);
    }
  };

  const loadGranola = async () => {
    try {
      const r = await api.get("/integrations/granola/status");
      setGranola(r.data);
    } catch { /* ignore */ }
  };

  const saveGranolaKey = async () => {
    if (!gKey.trim()) return;
    setGBusy(true);
    try {
      await api.put("/integrations/granola/key", { api_key: gKey.trim() });
      setGKey("");
      await loadGranola();
      toast.success("Granola connected — syncing your meetings now. They'll appear in Meetings shortly.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save the Granola key.");
    } finally {
      setGBusy(false);
    }
  };

  const syncGranolaNow = async () => {
    setGBusy(true);
    try {
      await api.post("/integrations/granola/sync");
      toast.success("Granola sync started — check Meetings in a minute.");
      await loadGranola();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start Granola sync.");
    } finally {
      setGBusy(false);
    }
  };

  const disconnectGranola = async () => {
    setGBusy(true);
    try {
      await api.delete("/integrations/granola");
      await loadGranola();
      toast("Granola disconnected. Synced meetings are kept.");
    } catch {
      toast.error("Couldn't disconnect Granola.");
    } finally {
      setGBusy(false);
    }
  };

  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    (async () => {
      await Promise.resolve();
      if (cancelled) return;
      setAsstName(user.assistant_name || "Andre");
      setWakeEnabled(!!user.wake_enabled);
      setWakeWord(user.wake_word || `Hey ${user.assistant_name || "Andre"}`);
    })();
    return () => { cancelled = true; };
  }, [user?.user_id, user?.assistant_name, user?.wake_enabled, user?.wake_word]);

  const saveAssistant = async () => {
    setSavingAsst(true);
    try {
      const phrase = wakeWord.trim() || `Hey ${asstName.trim() || "Andre"}`;
      await api.put("/settings/assistant", {
        assistant_name: asstName.trim() || "Andre",
        wake_word: phrase,
        wake_enabled: wakeEnabled,
      });
      await refresh();
      toast.success("Assistant settings saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save.");
    } finally {
      setSavingAsst(false);
    }
  };

  const speechSupported = typeof window !== "undefined" &&
    (window.SpeechRecognition || window.webkitSpeechRecognition);

  // Live wake status from the global listener
  const [wakeStatus, setWakeStatus] = useState("idle");
  const [wakeDetail, setWakeDetail] = useState("");
  useEffect(() => {
    const onStatus = (e) => {
      setWakeStatus(e.detail?.state || "idle");
      setWakeDetail(e.detail?.detail || "");
    };
    const onMatch = () => {
      toast.success("Heard the wake phrase. Opening voice mode.");
    };
    window.addEventListener("disciple:wake-status", onStatus);
    window.addEventListener("disciple:wake", onMatch);
    return () => {
      window.removeEventListener("disciple:wake-status", onStatus);
      window.removeEventListener("disciple:wake", onMatch);
    };
  }, []);

  const testWake = () => {
    // Simulate a wake event so user can verify the voice overlay opens
    window.dispatchEvent(new CustomEvent("disciple:wake", { detail: { phrase: "(test)" } }));
  };

  const STATUS_LABEL = {
    idle: { dot: "var(--ink-dim)", text: "Off" },
    listening: { dot: "var(--good)", text: `Listening for "${(wakeWord || `Hey ${asstName}`).trim()}"…` },
    denied: { dot: "var(--danger)", text: "Microphone blocked" },
    unsupported: { dot: "var(--ink-dim)", text: "Not supported by this browser" },
    error: { dot: "var(--gold)", text: `Listener error: ${wakeDetail || "unknown"}` },
  };
  const s = STATUS_LABEL[wakeStatus] || STATUS_LABEL.idle;

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings/commandments", { commandments });
      await refresh();
      toast.success("Commandments saved.");
    } catch {
      toast.error("Couldn't save.");
    } finally {
      setSaving(false);
    }
  };

  const toggleMemory = async () => {
    const next = !memEnabled;
    setMemEnabled(next);
    setMemBusy(true);
    try {
      await api.put("/settings/assistant", { memory_enabled: next });
      await refresh();
      toast.success(next ? `${asstName || "Andre"} will remember across chats.` : "Memory paused.");
    } catch {
      setMemEnabled(!next);
      toast.error("Couldn't update memory setting.");
    } finally {
      setMemBusy(false);
    }
  };

  const toggleAutoLearn = async () => {
    const next = !autoLearn;
    setAutoLearn(next);
    setAutoBusy(true);
    try {
      await api.put("/settings/assistant", { auto_learn_enabled: next });
      await refresh();
      toast.success(next
        ? `${asstName || "Andre"} will build people profiles automatically.`
        : "Auto-learn paused — new facts will wait for your review.");
    } catch {
      setAutoLearn(!next);
      toast.error("Couldn't update auto-learn setting.");
    } finally {
      setAutoBusy(false);
    }
  };

  const toggle = async (key, enabled) => {
    try {
      const r = await api.patch("/integrations", { key, enabled });
      setIntegrations(r.data.items);
    } catch { /* ignore */ }
  };

  const exportAll = async () => {
    try {
      const r = await api.get("/export");
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `players-health-export-${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Your vault is downloading.");
    } catch {
      toast.error("Couldn't export.");
    }
  };

  return (
    <>
      <div className="surface-header">
        <div className="surface-title">Settings</div>
        <span className="status-pill"><span className="dot" />{user?.email}</span>
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: "28px clamp(14px, 4vw, 32px) 60px" }}>
        <div style={{ maxWidth: 760, margin: "0 auto", display: "flex", flexDirection: "column", gap: 24 }}>
          {/* Your assistant (name + wake word) */}
          <section className="panel fade-up" style={{ padding: 24 }} data-testid="assistant-section">
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
              <Sparkles size={16} className="text-cobalt" />
              <div className="text-dim" style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase" }}>
                Your assistant
              </div>
            </div>
            <h3 style={{ marginBottom: 6 }}>What should I call you?</h3>
            <div className="text-soft" style={{ fontSize: 13.5, lineHeight: 1.6, marginBottom: 16 }}>
              {"Give your assistant a name — the one you'll say out loud when you want to be heard."}
            </div>

            <label className="text-dim" style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase" }}>
              Assistant name
              <input
                className="field"
                style={{ marginTop: 6, fontSize: 14, fontWeight: 500 }}
                value={asstName}
                onChange={(e) => {
                  setAsstName(e.target.value);
                  if (wakeWord === `Hey ${user?.assistant_name || "Andre"}` || !wakeWord.trim()) {
                    setWakeWord(`Hey ${e.target.value || "Andre"}`);
                  }
                }}
                maxLength={30}
                placeholder="Andre"
                data-testid="assistant-name-input"
              />
            </label>

            <div style={{ marginTop: 22, paddingTop: 22, borderTop: "1px solid var(--hairline-soft)" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Mic size={14} className="text-cobalt" />
                    <div style={{ fontWeight: 500, fontSize: 14 }}>Wake on phrase</div>
                  </div>
                  <div className="text-dim" style={{ fontSize: 12.5, marginTop: 4, lineHeight: 1.5 }}>
                    {speechSupported
                      ? `When enabled, ${asstName || "Andre"} listens quietly in the background. Say the wake phrase out loud to open voice mode hands-free.`
                      : "Your browser doesn't support background listening. Chrome or Edge on desktop is recommended."}
                  </div>
                </div>
                <div
                  className={`toggle ${wakeEnabled ? "on" : ""}`}
                  onClick={() => speechSupported && setWakeEnabled((v) => !v)}
                  data-testid="wake-toggle"
                  style={{ opacity: speechSupported ? 1 : 0.4, cursor: speechSupported ? "pointer" : "not-allowed" }}
                >
                  <div className="nub" />
                </div>
              </div>

              <label className="text-dim" style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", display: "block", marginTop: 16 }}>
                Wake phrase
                <input
                  className="field"
                  style={{ marginTop: 6, fontSize: 14, fontFamily: "ui-monospace, Menlo, monospace" }}
                  value={wakeWord}
                  onChange={(e) => setWakeWord(e.target.value)}
                  maxLength={60}
                  placeholder={`Hey ${asstName || "Andre"}`}
                  disabled={!wakeEnabled}
                  data-testid="wake-phrase-input"
                />
              </label>
              <div className="text-dim" style={{ fontSize: 11.5, marginTop: 8 }}>
                Tip: keep it two short words. Spoken phrases like <em>{`"Hey ${asstName || "Andre"}"`}</em>, <em>{`"OK ${asstName || "Andre"}"`}</em>, or <em>{`"Listen up"`}</em> all work.
              </div>

              {/* Live wake status */}
              <div
                data-testid="wake-status"
                style={{
                  marginTop: 14, padding: "10px 14px", borderRadius: 10,
                  background: "var(--panel-2)", border: "1px solid var(--hairline)",
                  display: "flex", alignItems: "center", gap: 10, fontSize: 12.5,
                }}
              >
                <span style={{
                  width: 8, height: 8, borderRadius: 50, background: s.dot,
                  boxShadow: wakeStatus === "listening" ? `0 0 8px ${s.dot}` : "none",
                  flexShrink: 0,
                }} />
                <span style={{ flex: 1, color: "var(--ink-soft)" }}>{s.text}</span>
                <button
                  onClick={testWake}
                  className="btn btn-ghost"
                  style={{ padding: "4px 10px", fontSize: 11, letterSpacing: "0.1em" }}
                  data-testid="test-wake-btn"
                  title="Simulate the wake phrase being heard"
                >
                  Test
                </button>
              </div>
              {wakeStatus === "denied" && (
                <div className="text-dim" style={{ fontSize: 11.5, marginTop: 8, lineHeight: 1.5 }}>
                  {"Mic access was denied. If you're in the preview iframe, click the lock icon in the URL bar and allow microphone — or open the app in a new tab via the share/open button."}
                </div>
              )}
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 18 }}>
              <button className="btn btn-primary" onClick={saveAssistant} disabled={savingAsst} data-testid="save-assistant-btn">
                {savingAsst ? "Saving…" : "Save assistant"}
              </button>
            </div>
          </section>

          {/* Memory */}
          <section className="panel fade-up" style={{ padding: 24 }} data-testid="memory-section">
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
              <Brain size={16} className="text-cobalt" />
              <div className="text-dim" style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase" }}>
                Memory
              </div>
            </div>
            <h3 style={{ marginBottom: 6 }}>{`${asstName || "Andre"} remembers, so you don't repeat yourself.`}</h3>
            <div className="text-soft" style={{ fontSize: 13.5, lineHeight: 1.6 }}>
              As you chat, {asstName || "Andre"} quietly notes durable facts — your preferences, ongoing work,
              key people, and decisions — and recalls them in every future conversation. It all lives in your
              Vault, so you can review, edit, or delete anything.
            </div>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, marginTop: 20, paddingTop: 20, borderTop: "1px solid var(--hairline-soft)" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 500, fontSize: 14 }}>Remember across conversations</div>
                <div className="text-dim" style={{ fontSize: 12.5, marginTop: 4, lineHeight: 1.5 }}>
                  {memEnabled
                    ? "On — new durable facts are saved automatically and silently."
                    : "Paused — nothing new will be remembered until you turn this back on."}
                </div>
              </div>
              <div
                className={`toggle ${memEnabled ? "on" : ""}`}
                onClick={() => !memBusy && toggleMemory()}
                data-testid="memory-toggle"
                style={{ cursor: memBusy ? "wait" : "pointer" }}
              >
                <div className="nub" />
              </div>
            </div>

            <div style={{ marginTop: 16 }}>
              <button
                className="btn"
                onClick={() => memDocId && navigate(`/vault/${memDocId}`)}
                disabled={!memDocId}
                data-testid="open-memory-vault-btn"
              >
                <BookText size={14} /> Review what {asstName || "Andre"} remembers
              </button>
            </div>
          </section>

          {/* Auto-learn people profiles */}
          <section className="panel fade-up" style={{ padding: 24 }} data-testid="autolearn-section">
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
              <Sparkles size={16} className="text-cobalt" />
              <div className="text-dim" style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase" }}>
                Auto-learn people
              </div>
            </div>
            <h3 style={{ marginBottom: 6 }}>{`${asstName || "Andre"} builds every person's profile for you.`}</h3>
            <div className="text-soft" style={{ fontSize: 13.5, lineHeight: 1.6 }}>
              As your Granola meetings sync, {asstName || "Andre"} pulls the durable facts — roles, teams,
              family, birthdays, locations — and adds them straight to each person's profile, so you never
              have to approve them one by one. Meeting chatter is ignored. Everything shows in a
              &ldquo;Recently learned&rdquo; feed on the People page, where you can undo anything.
            </div>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, marginTop: 20, paddingTop: 20, borderTop: "1px solid var(--hairline-soft)" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 500, fontSize: 14 }}>Auto-add profile facts</div>
                <div className="text-dim" style={{ fontSize: 12.5, marginTop: 4, lineHeight: 1.5 }}>
                  {autoLearn
                    ? "On — durable facts are added automatically. Undo any of them on the People page."
                    : "Paused — new facts wait for your approval on the People page instead."}
                </div>
              </div>
              <div
                className={`toggle ${autoLearn ? "on" : ""}`}
                onClick={() => !autoBusy && toggleAutoLearn()}
                data-testid="autolearn-toggle"
                style={{ cursor: autoBusy ? "wait" : "pointer" }}
              >
                <div className="nub" />
              </div>
            </div>
          </section>

          {/* Commandments */}
          <section className="panel fade-up" style={{ padding: 24 }} data-testid="commandments-section">
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
              <ShieldCheck size={16} className="text-gold" />
              <div className="text-dim" style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase" }}>
                Core Values
              </div>
            </div>
            <h3 style={{ marginBottom: 6 }}>The principles that govern Andre.</h3>
            <div className="font-serif-italic text-soft" style={{ fontSize: 14, borderLeft: "2px solid var(--gold)", paddingLeft: 14, margin: "12px 0 18px" }}>
              {"These are inviolable. Andre refuses any request that would break them — regardless of how it's phrased."}
            </div>
            <textarea
              className="field"
              style={{ minHeight: 320, fontFamily: "ui-monospace, Menlo, monospace", fontSize: 13 }}
              value={commandments}
              onChange={(e) => setCommandments(e.target.value)}
              data-testid="commandments-textarea"
            />
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}>
              <button className="btn btn-primary" onClick={save} disabled={saving} data-testid="save-commandments-btn">
                {saving ? "Saving…" : "Save commandments"}
              </button>
            </div>
          </section>

          {/* Microsoft 365 connection */}
          <section className="panel fade-up" style={{ padding: 24 }} data-testid="ms365-connect-section">
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
              <span className="ms365-logo" aria-hidden="true">
                <svg width="22" height="22" viewBox="0 0 23 23">
                  <rect x="1" y="1" width="10" height="10" fill="#F25022" />
                  <rect x="12" y="1" width="10" height="10" fill="#7FBA00" />
                  <rect x="1" y="12" width="10" height="10" fill="#00A4EF" />
                  <rect x="12" y="12" width="10" height="10" fill="#FFB900" />
                </svg>
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h3 style={{ margin: 0 }}>Microsoft 365</h3>
                <div className="text-dim" style={{ fontSize: 12.5, marginTop: 2 }}>
                  Let {asstName || "Andre"} read your email &amp; calendar — read-only, drafts only.
                </div>
              </div>
              {msStatus?.connected && msStatus.needs_reconnect ? (
                <span
                  className="status-pill"
                  data-testid="ms365-needs-reconnect-pill"
                  style={{
                    flexShrink: 0,
                    background: "rgba(245,158,11,0.10)",
                    border: "1px solid rgba(245,158,11,0.40)",
                    color: "rgb(180,110,4)",
                  }}
                >
                  <span className="dot" style={{ background: "rgb(245,158,11)" }} /> Needs reconnect
                </span>
              ) : msStatus?.connected ? (
                <span className="status-pill" data-testid="ms365-connected-pill" style={{ flexShrink: 0 }}>
                  <span className="dot" style={{ background: "var(--good)" }} /> Connected
                </span>
              ) : null}
            </div>

            {msStatus?.connected && msStatus.needs_reconnect && (
              <div
                data-testid="ms365-needs-reconnect-banner"
                style={{
                  marginTop: 14, padding: "12px 14px",
                  borderRadius: 10,
                  background: "rgba(245,158,11,0.07)",
                  border: "1px solid rgba(245,158,11,0.30)",
                  fontSize: 13, lineHeight: 1.55, color: "var(--ink-soft)",
                }}
              >
                Your Microsoft 365 connection has expired (consent change, password reset, or token aged out). {asstName || "Andre"} can't read your live calendar or email until you reconnect — your previous synced data is safe.
                <div style={{ marginTop: 10 }}>
                  <button
                    className="btn btn-primary"
                    onClick={connectMicrosoft}
                    disabled={msBusy}
                    data-testid="ms365-reconnect-btn"
                  >
                    {msBusy ? <Loader2 size={14} className="spin" /> : <Link2 size={14} />} Reconnect Microsoft 365
                  </button>
                </div>
              </div>
            )}

            {!msStatus && (
              <div className="text-dim" style={{ fontSize: 13 }}>Checking connection…</div>
            )}

            {msStatus && !msStatus.configured && (
              <div className="ms365-note" data-testid="ms365-not-configured">
                {"Microsoft sign-in isn't configured for this workspace yet. An administrator must add the Azure app credentials before mailboxes can be linked."}
              </div>
            )}

            {msStatus && msStatus.configured && !msStatus.connected && (
              <div>
                <div className="ms365-scopes">
                  <span><Mail size={13} /> Read mail</span>
                  <span><Calendar size={13} /> Read calendar</span>
                  <span><ShieldCheck size={13} /> Never sends</span>
                </div>
                <button className="btn btn-primary" onClick={connectMicrosoft} disabled={msBusy} data-testid="ms365-connect-btn" style={{ marginTop: 16 }}>
                  {msBusy ? <Loader2 size={15} className="spin" /> : <Link2 size={15} />}
                  {msBusy ? "Redirecting…" : "Connect Microsoft 365"}
                </button>
              </div>
            )}

            {msStatus && msStatus.connected && !msStatus.needs_reconnect && (
              <div>
                <div className="text-soft" style={{ fontSize: 13.5 }}>
                  Connected as <strong style={{ color: "var(--ink)" }}>{msStatus.email || "your account"}</strong>.
                  Open the <a href="/inbox" style={{ color: "var(--ph-product-teal)" }}>Inbox</a> to read your mail &amp; calendar.
                </div>
                <button className="btn" onClick={disconnectMicrosoft} disabled={msBusy} data-testid="ms365-disconnect-btn" style={{ marginTop: 16 }}>
                  {msBusy ? <Loader2 size={14} className="spin" /> : <Unplug size={14} />} Disconnect
                </button>
              </div>
            )}
          </section>

          {/* Granola meeting notes (workspace-level) */}
          <section className="panel fade-up" style={{ padding: 24 }} data-testid="granola-section">
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
              <span aria-hidden="true" style={{
                width: 32, height: 32, borderRadius: 8, display: "inline-flex", alignItems: "center",
                justifyContent: "center", background: "rgba(78,205,196,0.14)",
                border: "1px solid rgba(78,205,196,0.4)", color: "var(--ph-product-teal)",
                fontWeight: 800, fontSize: 15, flexShrink: 0,
              }}>G</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h3 style={{ margin: 0 }}>Granola meeting notes</h3>
                <div className="text-dim" style={{ fontSize: 12.5, marginTop: 2 }}>
                  Connect your Granola account — your meetings, AI notes &amp; transcripts sync into Meetings every 15 minutes.
                </div>
              </div>
              {granola?.configured && (
                <span className="status-pill" data-testid="granola-connected-pill" style={{ flexShrink: 0 }}>
                  <span className="dot" style={{ background: "var(--good)" }} /> Connected
                </span>
              )}
            </div>

            {!granola && <div className="text-dim" style={{ fontSize: 13 }}>Checking connection…</div>}

            {granola && granola.configured && (
              <div>
                <div className="text-soft" style={{ fontSize: 13.5 }} data-testid="granola-status-line">
                  <strong style={{ color: "var(--ink)" }}>{granola.meeting_count}</strong> meeting{granola.meeting_count === 1 ? "" : "s"} synced
                  {granola.last_sync_at && <> · last sync {new Date(granola.last_sync_at).toLocaleString()}</>}
                  {granola.sync_state === "running" && <> · <span style={{ color: "var(--ph-product-teal)" }}>syncing now…</span></>}
                </div>
                {granola.last_error && (
                  <div style={{ fontSize: 12.5, color: "var(--bad, #e5605e)", marginTop: 8 }} data-testid="granola-error">
                    Last sync issue: {granola.last_error}
                  </div>
                )}
                <div style={{ display: "flex", gap: 10, marginTop: 16, flexWrap: "wrap" }}>
                  <button className="btn btn-primary" onClick={syncGranolaNow} disabled={gBusy || granola.sync_state === "running"} data-testid="granola-sync-now-btn">
                    {gBusy ? <Loader2 size={14} className="spin" /> : <Link2 size={14} />} Sync now
                  </button>
                  <button className="btn" onClick={disconnectGranola} disabled={gBusy} data-testid="granola-disconnect-btn">
                    <Unplug size={14} /> Disconnect
                  </button>
                </div>
              </div>
            )}

            {granola && !granola.configured && (
              <div>
                <div className="text-dim" style={{ fontSize: 13, marginBottom: 12 }}>
                  Paste your Granola API key (Granola app → Settings → Connectors → API keys). Requires a Granola Business or Enterprise plan.
                </div>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  <input
                    className="input"
                    type="password"
                    placeholder="grn_…"
                    value={gKey}
                    onChange={(e) => setGKey(e.target.value)}
                    data-testid="granola-key-input"
                    style={{ flex: 1, minWidth: 220 }}
                  />
                  <button className="btn btn-primary" onClick={saveGranolaKey} disabled={gBusy || !gKey.trim()} data-testid="granola-save-key-btn">
                    {gBusy ? <Loader2 size={14} className="spin" /> : <Link2 size={14} />} Connect
                  </button>
                </div>
              </div>
            )}
          </section>

          {/* Integrations */}
          <section data-testid="integrations-section">
            <div className="text-dim" style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 10 }}>
              Integrations
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {integrations.map((it) => (
                <div key={it.key} className="toggle-row" data-testid={`integration-${it.key}`}>
                  <div>
                    <div style={{ fontWeight: 500 }}>{it.label}</div>
                    <div className="text-dim" style={{ fontSize: 12, marginTop: 2 }}>
                      {it.enabled ? "Connected" : "Disconnected"} · mocked in v1
                    </div>
                  </div>
                  <div
                    className={`toggle ${it.enabled ? "on" : ""}`}
                    onClick={() => toggle(it.key, !it.enabled)}
                    data-testid={`toggle-${it.key}`}
                  >
                    <div className="nub" />
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Privacy */}
          <section className="panel" style={{ padding: 24 }} data-testid="privacy-section">
            <div className="text-dim" style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 10 }}>
              Privacy
            </div>
            <h3>Your data is yours.</h3>
            <div className="text-soft" style={{ fontSize: 13.5, marginTop: 10, lineHeight: 1.65 }}>
              Your vault is local-first. We never train on your content. You can export everything, anytime.
            </div>
            <div style={{ marginTop: 16 }}>
              <button className="btn" onClick={exportAll} data-testid="export-btn">
                <Download size={14} /> Export everything
              </button>
            </div>
          </section>
        </div>
      </div>
    </>
  );
}
